import type { FC } from "react";
import { useForm, useStore } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldInput, FieldMultipleCheckbox } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import HealthModalFooter from "@/components/modals/profil-health/health-modal-footer";
import type { HealthUpdateModalProps } from "@/components/modals/profil-health/health-update-modal";
import { Separator } from "@/components/ui/separator";
import type { PatchPathologiesFormData } from "@/schemas/patient";
import { patchPathologiesSchema } from "@/schemas/patient";
import { resetForm } from "@/utils/form";
import { pathologiesOptions } from "@/utils/patient";

const PathologiesModal: FC<HealthUpdateModalProps> = ({
  healthModalOpen,
  setHealthModalOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { associatedPathologies, pathologyComment } = patient;

  const { Field, handleSubmit, store } = useForm({
    defaultValues: {
      associatedPathologies,
      pathologyComment,
    } as PatchPathologiesFormData,
    onSubmit: async ({ value, formApi }) => {
      const values = patchPathologiesSchema.parse(value);
      if (values.associatedPathologies === null) {
        values.associatedPathologies = [];
      }
      mutate(values, {
        onSuccess: () => {
          setHealthModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  const associatedPathologiesValues = useStore(
    store,
    (state) => state.values.associatedPathologies
  );

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      open={healthModalOpen !== "none"}
      onOpenChange={() => setHealthModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldMultipleCheckbox
          Field={Field}
          schema={patchPathologiesSchema}
          name="associatedPathologies"
          options={pathologiesOptions}
          validators={{
            onChange: patchPathologiesSchema.shape.associatedPathologies,
          }}
        />
        {associatedPathologiesValues?.includes("OTHER") ? (
          <FieldInput
            Field={Field}
            schema={patchPathologiesSchema}
            name="pathologyComment"
            placeholder="Ajouter un commentaire"
            validators={{
              onChange: patchPathologiesSchema.shape.pathologyComment,
            }}
          />
        ) : null}
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <HealthModalFooter
        setHealthModalOpen={setHealthModalOpen}
        handleSubmit={handleSubmit}
      />
    </DialogCustom>
  );
};

export default PathologiesModal;
